import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

type Theme = "dark" | "light";

const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({
  theme: "dark",
  toggle: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const saved = (localStorage.getItem("pw-theme") as Theme) ?? "dark";
      // Apply synchronously before first render so getComputedStyle is correct
      document.documentElement.setAttribute("data-theme", saved);
      return saved;
    } catch {
      return "dark";
    }
  });

  function toggle() {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("pw-theme", next);
    } catch {}
    setTheme(next);
  }

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function useChartColors() {
  const { theme } = useTheme();
  return {
    grid: theme === "light" ? "#e2e8f0" : "#1f2329",
    text: theme === "light" ? "#64748b" : "#6b7280",
  };
}
